<script setup lang="ts">
import { computed } from "vue";
import { useHead } from "@unhead/vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

useHead(
  computed(() => ({
    title: t("termsOfSale.meta.title"),
    meta: [
      { name: "description", content: t("termsOfSale.meta.description") },
      { name: "robots", content: "index,follow" },
    ],
    htmlAttrs: { lang: locale.value },
  })),
);
</script>

<template>
  <article class="mx-auto max-w-[720px] px-5 py-12 md:px-8 md:py-20">
    <router-link
      to="/"
      class="inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
    >
      <UIcon name="i-lucide-arrow-left" class="size-4" />
      {{ t("termsOfSale.backToHome") }}
    </router-link>

    <h1
      class="mt-6 text-3xl font-semibold text-[var(--color-text-primary)] md:text-4xl"
      style="font-family: var(--font-display)"
    >
      {{ t("termsOfSale.heading") }}
    </h1>
    <p class="mt-2 text-sm text-[var(--color-text-muted)]">
      {{ t("termsOfSale.lastUpdated") }}
    </p>

    <div class="mt-10 space-y-10 text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section1.heading") }}
        </h2>
        <p class="mt-3">{{ t("termsOfSale.section1.p1") }}</p>
        <p class="mt-3">
          <strong>HeartQuake Sagl</strong><br />
          Via Rivascia 11<br />
          6518 Gorduno, Switzerland<br />
          {{ t("termsOfSale.section1.contactLabel") }}
          <a href="mailto:hardware@cresci.kids" class="underline">hardware@cresci.kids</a>
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section2.heading") }}
        </h2>
        <p class="mt-3">{{ t("termsOfSale.section2.p1") }}</p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section3.heading") }}
        </h2>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>
            {{ t("termsOfSale.section3.item1Before") }}
            <strong>{{ t("termsOfSale.section3.item1Price") }}</strong
            >{{ t("termsOfSale.section3.item1After") }}
          </li>
          <li>{{ t("termsOfSale.section3.item2") }}</li>
          <li>{{ t("termsOfSale.section3.item3") }}</li>
          <li>{{ t("termsOfSale.section3.item4") }}</li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section4.heading") }}
        </h2>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>{{ t("termsOfSale.section4.item1") }}</li>
          <li>{{ t("termsOfSale.section4.item2") }}</li>
          <li>
            <strong>{{ t("termsOfSale.section4.item3Strong") }}</strong>
            {{ t("termsOfSale.section4.item3Body") }}
          </li>
          <li>{{ t("termsOfSale.section4.item4") }}</li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section5.heading") }}
        </h2>
        <p class="mt-3">{{ t("termsOfSale.section5.p1") }}</p>
        <p class="mt-3">
          <strong>{{ t("termsOfSale.section5.exerciseHeading") }}</strong>
        </p>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>
            {{ t("termsOfSale.section5.item1Before") }}
            <a href="mailto:hardware@cresci.kids" class="underline">hardware@cresci.kids</a>
            {{ t("termsOfSale.section5.item1After") }}
          </li>
          <li>{{ t("termsOfSale.section5.item2") }}</li>
          <li>{{ t("termsOfSale.section5.item3") }}</li>
          <li>{{ t("termsOfSale.section5.item4") }}</li>
          <li>
            {{ t("termsOfSale.section5.item5Before") }}
            <strong>{{ t("termsOfSale.section5.item5Strong") }}</strong
            >{{ t("termsOfSale.section5.item5After") }}
          </li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section6.heading") }}
        </h2>
        <p class="mt-3">{{ t("termsOfSale.section6.p1") }}</p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section7.heading") }}
        </h2>
        <p class="mt-3">{{ t("termsOfSale.section7.p1") }}</p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section8.heading") }}
        </h2>
        <p class="mt-3">{{ t("termsOfSale.section8.p1") }}</p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section9.heading") }}
        </h2>
        <p class="mt-3">
          {{ t("termsOfSale.section9.p1Before") }}
          <a href="https://ec.europa.eu/consumers/odr" class="underline" rel="noopener"
            >ec.europa.eu/consumers/odr</a
          >{{ t("termsOfSale.section9.p1After") }}
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("termsOfSale.section10.heading") }}
        </h2>
        <p class="mt-3">{{ t("termsOfSale.section10.p1") }}</p>
      </section>
    </div>
  </article>
</template>
